import { useState } from 'react';
import { createLogger } from '../utils/logger';

const logger = createLogger('useScreenshotCapture');

const MAX_SCREENSHOT_SIZE_MB = 5;

interface ScreenshotCapture {
  handleFileUpload: (file: File) => void;
  screenshot: string | null;
  screenshotError: string | null;
  isCapturingScreenshot: boolean;
  setScreenshot: (screenshot: string | null) => void;
  setScreenshotError: (error: string | null) => void;
  resetScreenshot: () => void;
}

export const useScreenshotCapture = (): ScreenshotCapture => {
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [screenshotError, setScreenshotError] = useState<string | null>(null);
  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);

  const handleFileUpload = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setScreenshotError('Please upload an image file');
      return;
    }

    if (file.size > MAX_SCREENSHOT_SIZE_MB * 1024 * 1024) {
      setScreenshotError(`Image must be under ${MAX_SCREENSHOT_SIZE_MB}MB`);
      return;
    }

    setIsCapturingScreenshot(true);
    setScreenshotError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const dataURL = e.target?.result as string;
      if (dataURL) {
        setScreenshot(dataURL);
      } else {
        setScreenshotError('Failed to read image file');
      }
      setIsCapturingScreenshot(false);
    };
    reader.onerror = () => {
      logger.error('FileReader failed:', reader.error);
      setScreenshotError('Failed to read image file');
      setIsCapturingScreenshot(false);
    };
    reader.readAsDataURL(file);
  };

  const resetScreenshot = () => {
    setScreenshot(null);
    setScreenshotError(null);
    setIsCapturingScreenshot(false);
  };

  return {
    handleFileUpload,
    screenshot,
    screenshotError,
    isCapturingScreenshot,
    setScreenshot,
    setScreenshotError,
    resetScreenshot
  };
};